import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface CustomerStory {
  id: number;
  customer_name: string;
  customer_avatar?: string;
  customer_location?: string;
  experience_name: string;
  experience_image?: string;
  rating: number;
  review_text: string;
  review_date: string;
  verified_purchase: boolean;
  helpful_count?: number;
  photos?: string[];
  href?: string;
}

interface CustomerStoriesSectionProps {
  stories?: CustomerStory[];
  className?: string;
}

/**
 * PLACEHOLDER SECTION.
 *
 * The `customer_stories` prop is mock data (`SiteInertiaController::getMockCustomerStories`)
 * and the application has no reviews feature — the `reviews` table has no model, no
 * writer and no reader. So this section renders the *layout* only: no names, no avatars,
 * no star ratings and no "verified purchase" marks, because every one of those would be
 * an invented fact presented as a measurement.
 *
 * When real reviews land, swap the data source and restore attribution — the prop
 * signature is unchanged.
 */
export function CustomerStoriesSection({ stories, className }: CustomerStoriesSectionProps) {
  const items = (stories ?? []).slice(0, 3);
  if (items.length === 0) return null;

  return (
    <section className={cn('relative', className)} aria-labelledby="customer-stories-heading">
      <header className="mb-4 flex items-end justify-between gap-4 md:mb-5">
        <div className="flex min-w-0 items-center gap-3">
          <span aria-hidden="true" className="block h-6 w-[3px] shrink-0 rounded-full" style={{ backgroundColor: 'var(--section-accent)' }} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="customer-stories-heading" className="text-xl leading-tight font-bold text-gray-900 md:text-2xl">
                Câu chuyện khách hàng
              </h2>
              <Badge variant="secondary" className="rounded-full">
                Sắp ra mắt
              </Badge>
            </div>
            <p className="mt-0.5 text-sm text-gray-500">Chúng tôi đang thu thập đánh giá thật từ khách hàng đã hoàn tất đặt vé.</p>
          </div>
        </div>
      </header>

      <p className="sr-only">Nội dung dưới đây là bố cục minh hoạ, chưa phải đánh giá thật của khách hàng.</p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" aria-hidden="true">
        {items.map((story) => (
          <Card key={story.id} className="border-dashed border-gray-200 bg-white/60 shadow-none">
            <CardContent className="p-5">
              <div className="mb-4 flex items-center gap-3">
                <span className="size-10 shrink-0 rounded-full" style={{ backgroundColor: 'var(--surface-muted)' }} />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-400">Khách hàng đã đặt vé</p>
                  <span className="mt-1 block h-2 w-24 rounded-full" style={{ backgroundColor: 'var(--surface-muted)' }} />
                </div>
              </div>
              <p className="line-clamp-4 text-sm leading-relaxed text-gray-500 italic">{story.review_text}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
